import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, CheckCircle, XCircle, Trash, Calendar, Fire, Lightning } from "@phosphor-icons/react";

const PRIORITY_COLORS = { low: "var(--text-3)", normal: "var(--text-2)", high: "#FF9500", urgent: "#FF3B30" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [contacts, setContacts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_at: "", priority: "normal", contact_id: "" });

  const load = async () => {
    const r = await api.get("/tasks", { params: { status: statusFilter || undefined, limit: 300 } });
    setTasks(r.data.tasks || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      const payload = { ...form };
      if (!payload.contact_id) delete payload.contact_id;
      if (!payload.due_at) delete payload.due_at;
      await api.post("/tasks", payload);
      setForm({ title: "", description: "", due_at: "", priority: "normal", contact_id: "" });
      setShowNew(false);
      toast.success("Task created");
      load();
    } catch { toast.error("Could not create task"); }
  };

  const toggleComplete = async (t) => {
    try {
      await api.put(`/tasks/${t.id}`, { status: t.status === "completed" ? "pending" : "completed" });
      load();
    } catch { toast.error("Update failed"); }
  };

  const cancel = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: "cancelled" });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${t.id}`);
    load();
  };

  const contactName = (cid) => contacts.find((c) => c.id === cid)?.name;

  return (
    <div className="p-6 md:p-10" data-testid="tasks-page">
      <Toaster theme="dark" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">// tasks</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Your tasks</h1>
        </div>
        <button onClick={() => setShowNew(!showNew)} data-testid="new-task-btn" className="btn-primary flex items-center gap-2">
          <Plus weight="bold" /> New task
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {["pending", "completed", "cancelled", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            data-testid={`filter-${s || "all"}`}
            className="tag cursor-pointer"
            style={statusFilter === s ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}
          >
            {s || "all"}
          </button>
        ))}
      </div>

      {showNew && (
        <form onSubmit={create} className="border p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" style={{ borderColor: "var(--border)" }} data-testid="new-task-form">
          <div className="md:col-span-2">
            <label className="label-base">Title *</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-base" data-testid="task-title" />
          </div>
          <div className="md:col-span-2">
            <label className="label-base">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="input-base" data-testid="task-description" />
          </div>
          <div>
            <label className="label-base">Due date</label>
            <input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="input-base" data-testid="task-due-at" />
          </div>
          <div>
            <label className="label-base">Priority</label>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="task-priority">
              {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-base">Linked contact (optional)</label>
            <select value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="task-contact">
              <option value="">— no contact —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-primary" data-testid="task-submit">Save task</button>
            <button type="button" className="btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="border" style={{ borderColor: "var(--border)" }}>
        {tasks.length === 0 && (
          <div className="p-10 text-center">
            <div className="overline mb-3">// no tasks</div>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Create a task to track something you need to do.</p>
          </div>
        )}
        {tasks.map((t, i) => (
          <div key={t.id} className={`p-4 flex items-center justify-between gap-3 ${i > 0 ? "border-t" : ""}`} style={{ borderColor: "var(--border)" }} data-testid={`task-row-${t.id}`}>
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <button
                onClick={() => toggleComplete(t)}
                className="mt-1 flex-shrink-0"
                data-testid={`toggle-task-${t.id}`}
                aria-label="Toggle complete"
              >
                {t.status === "completed" ? (
                  <CheckCircle weight="fill" size={22} style={{ color: "var(--accent)" }} />
                ) : (
                  <div style={{ width: 22, height: 22, border: "2px solid var(--border-2)", borderRadius: "50%" }} />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold ${t.status === "completed" ? "line-through opacity-60" : ""}`}>{t.title}</span>
                  <span className="tag" style={{ color: PRIORITY_COLORS[t.priority], borderColor: PRIORITY_COLORS[t.priority] }}>{t.priority}</span>
                  {t.status === "cancelled" && <span className="tag">CANCELLED</span>}
                  {t.contact_id && (
                    <Link to={`/contacts/${t.contact_id}`} className="tag flex items-center gap-1" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>
                      → {contactName(t.contact_id) || "contact"}
                    </Link>
                  )}
                </div>
                {t.description && <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{t.description}</div>}
                {t.due_at && <div className="overline mt-1 flex items-center gap-1"><Calendar weight="bold" /> {new Date(t.due_at).toLocaleDateString()}</div>}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {t.status === "pending" && (
                <button onClick={() => cancel(t)} className="btn-secondary text-xs px-2 py-1" data-testid={`cancel-task-${t.id}`}><XCircle weight="bold" /></button>
              )}
              <button onClick={() => remove(t)} className="btn-secondary text-xs px-2 py-1" data-testid={`delete-task-${t.id}`}><Trash weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
